import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, Zap, Lightbulb, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface AIQueryToolsProps {
  databaseId: string;
  query: string;
  executionTime?: number;
  onQueryUpdate?: (sql: string) => void;
}

export function AIQueryTools({ databaseId, query, executionTime, onQueryUpdate }: AIQueryToolsProps) {
  const [explaining, setExplaining] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [explanation, setExplanation] = useState<{ explanation: string; complexity: string } | null>(null);
  const [optimization, setOptimization] = useState<{ suggestions: string[]; optimizedSQL?: string; estimatedImprovement?: string } | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showOptimization, setShowOptimization] = useState(false);

  const handleExplain = async () => {
    if (!query.trim()) {
      toast.error('No query to explain');
      return;
    }

    setExplaining(true);
    try {
      const result = await api.explainQuery(databaseId, query);
      setExplanation(result);
      setShowExplanation(true);
    } catch (error: any) {
      toast.error(error.message || 'Failed to explain query');
    } finally {
      setExplaining(false);
    }
  };

  const handleOptimize = async () => {
    if (!query.trim()) {
      toast.error('No query to optimize');
      return;
    }

    setOptimizing(true);
    try {
      const result = await api.optimizeQuery(databaseId, query, executionTime || 0);
      setOptimization(result);
      setShowOptimization(true);
    } catch (error: any) {
      toast.error(error.message || 'Failed to optimize query');
    } finally {
      setOptimizing(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleExplain}
          disabled={explaining || !query.trim()}
        >
          {explaining ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Explaining
            </>
          ) : (
            <>
              <Lightbulb className="mr-2 h-4 w-4" />
              Explain
            </>
          )}
        </Button>

        {executionTime && executionTime > 1000 && (
          <Badge
            variant="outline"
            className="cursor-pointer hover:bg-accent"
            onClick={handleOptimize}
          >
            <Zap className="mr-1 h-3 w-3" />
            Slow Query - Optimize
          </Badge>
        )}

        {!executionTime && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleOptimize}
            disabled={optimizing || !query.trim()}
          >
            {optimizing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Optimizing
              </>
            ) : (
              <>
                <Zap className="mr-2 h-4 w-4" />
                Optimize
              </>
            )}
          </Button>
        )}
      </div>

      <Dialog open={showExplanation} onOpenChange={setShowExplanation}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Query Explanation
            </DialogTitle>
            <DialogDescription>
              AI-powered explanation of your SQL query
            </DialogDescription>
          </DialogHeader>
          {explanation && (
            <div className="space-y-4">
              <div>
                <Badge variant={
                  explanation.complexity === 'simple' ? 'default' :
                  explanation.complexity === 'moderate' ? 'secondary' : 'destructive'
                }>
                  {explanation.complexity} query
                </Badge>
              </div>
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm leading-relaxed">{explanation.explanation}</p>
              </div>
              <div className="rounded-lg border bg-card p-3">
                <p className="text-xs font-mono text-muted-foreground">{query}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showOptimization} onOpenChange={setShowOptimization}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-600" />
              Query Optimization
            </DialogTitle>
            <DialogDescription>
              AI-powered suggestions to improve query performance
            </DialogDescription>
          </DialogHeader>
          {optimization && (
            <ScrollArea className="max-h-[500px]">
              <div className="space-y-4">
                {optimization.estimatedImprovement && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Estimated Improvement</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-lg font-semibold text-green-600">
                        {optimization.estimatedImprovement}
                      </p>
                    </CardContent>
                  </Card>
                )}

                <div>
                  <h4 className="text-sm font-semibold mb-2">Suggestions:</h4>
                  <ul className="space-y-2">
                    {optimization.suggestions.map((suggestion, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-purple-600 mt-1">•</span>
                        <span className="text-sm">{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {optimization.optimizedSQL && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Optimized Query</CardTitle>
                      <CardDescription>AI-generated optimized version</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-lg border bg-muted p-3 mb-3">
                        <pre className="text-xs font-mono whitespace-pre-wrap">
                          {optimization.optimizedSQL}
                        </pre>
                      </div>
                      {onQueryUpdate && (
                        <Button
                          size="sm"
                          onClick={() => {
                            onQueryUpdate(optimization.optimizedSQL!);
                            setShowOptimization(false);
                            toast.success('Optimized query applied');
                          }}
                        >
                          <Sparkles className="mr-2 h-4 w-4" />
                          Apply Optimized Query
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
